import { StudentPagination, StudentPaginationParameters } from "@carnegie-learning/data-access-angular-passport";
import { Action, createReducer, on } from "@ngrx/store";
import { CourseMetrics, Student, User } from "cli-types";
import * as fromStudentsActions from "../actions/students.action";

export interface StudentsListState {
    entities: Map<string, Student>;
    studentMetrics: CourseMetrics | null;
    loaded: boolean;
    loading: boolean;
    rawResponse: StudentPagination | null;
    paginationParams: StudentPaginationParameters;
    filterBoxOpened: boolean;
    fidoStudentsData: User[];
}

export const initialState: StudentsListState = {
    entities: new Map<string, Student>(),
    studentMetrics: null,
    loaded: false,
    loading: false,
    rawResponse: null,
    paginationParams: {
        limit: 100,
        page: 0,
        filter: { courseArchived: false },
        search: "",
        sorting: { lastName: "asc" },
    },
    filterBoxOpened: false,
    fidoStudentsData: [],
};
const studentsListReducer = createReducer(
    initialState,
    on(
        fromStudentsActions.loadStudents,
        fromStudentsActions.loadStudent,
        fromStudentsActions.loadStudentMetrics,
        fromStudentsActions.addStudents,
        fromStudentsActions.loadStudentsFidoData,
        (state) => {
            return {
                ...state,
                loading: true,
                loaded: false,
            };
        }
    ),
    on(fromStudentsActions.loadStudentsSuccess, (state, { payload }) => {
        const students = payload.data;

        const entities = new Map<string, Student>();
        students.forEach((student) => {
            entities.set(student.userGUID, student);
        });

        return {
            ...state,
            loaded: true,
            loading: false,
            entities,
            rawResponse: payload,
        };
    }),
    on(fromStudentsActions.removeStudents, (state) => {
        const emptyMap = new Map<string, Student>();

        return {
            ...state,
            loaded: true,
            loading: false,
            entities: emptyMap,
        };
    }),
    on(fromStudentsActions.setStudentPaginationParams, (state, { payload }) => {
        // Merge parameter values, don't overwrite like LOAD_RESOURCES does.
        const inputParams = payload;
        const studentPaginationParams: StudentPaginationParameters = {
            ...state.paginationParams,
        };
        const filter = {};
        Object.keys(inputParams).forEach(function (key) {
            (<any>studentPaginationParams)[key] = inputParams[key]; //TODO: Find a better answer than this
        });

        return {
            ...state,
            paginationParams: studentPaginationParams,
        };
    }),
    on(fromStudentsActions.loadStudentSuccess, (state, { payload }) => {
        const student = payload;
        const entities = new Map<string, Student>(state.entities.entries());
        entities.set(student.userGUID, student);

        return {
            ...state,
            loaded: true,
            loading: false,
            entities,
        };
    }),
    on(fromStudentsActions.loadStudentMetricsSuccess, (state, { payload }) => {
        const sMetrics = payload;
        return {
            ...state,
            studentMetrics: sMetrics,
            loaded: false,
            loading: false,
        };
    }),
    on(
        fromStudentsActions.loadStudentsFail,
        fromStudentsActions.loadStudentFail,
        fromStudentsActions.loadStudentMetricsFail,
        (state) => {
            return {
                ...state,
                loaded: false,
                loading: false,
            };
        }
    ),
    on(fromStudentsActions.addStudentSuccess, (state, { payload }) => {
        const student = payload;

        const entities = new Map<string, Student>(state.entities.entries());
        entities.set(student.userGUID, student);

        return {
            ...state,
            entities,
        };
    }),
    on(fromStudentsActions.addStudentsSuccess, (state, { payload }) => {
        const students = payload; // array of students
        const entities = new Map<string, Student>(state.entities.entries());
        students.forEach((student) => {
            const stateStudent = entities.get(student.userGUID);
            let updatedStudent: Student;
            if (stateStudent) {
                const {
                    firstName,
                    lastName,
                    eAddress,
                    ...studentDataWithoutNames
                } = student;
                updatedStudent = {
                    ...stateStudent,
                    ...studentDataWithoutNames,
                };
            } else {
                updatedStudent = student;
            }

            entities.set(student.userGUID, updatedStudent);
        });

        return {
            ...state,
            entities,
        };
    }),
    on(
        fromStudentsActions.removeStudentSuccess,
        fromStudentsActions.reactivateStudentSuccess,
        fromStudentsActions.archiveStudentSuccess,
        (state, { payload }) => {
            const student = payload;

            const entities = new Map<string, Student>(state.entities.entries());
            entities.delete(student.userGUID);

            return {
                ...state,
                entities,
            };
        }
    ),
    on(fromStudentsActions.setStudentFilterBoxOpened, (state, { payload }) => {
        return {
            ...state,
            filterBoxOpened: payload,
        };
    }),
    on(fromStudentsActions.resetStudentsStore, (state) => {
        return initialState;
    }),
    on(
        fromStudentsActions.loadStudentsFidoDataSuccess,
        (state, { fidoData }) => {
            const entities = new Map<string, Student>(state.entities.entries());

            fidoData.forEach((fidoUser: User) => {
                const originalStudent = state.entities.get(fidoUser.id);
                const student = <Student>{
                    ...originalStudent,
                    firstName: fidoUser.givenName,
                    lastName: fidoUser.surName,
                    eAddress: fidoUser.email,
                };
                entities.set(fidoUser.id, student);
            });

            entities.forEach((entity) => {
                if (entity.firstName === null) {
                    entities.delete(entity.userGUID);
                }
            });

            return {
                ...state,
                loaded: true,
                loading: false,
                entities,
                fidoStudentsData: fidoData,
            };
        }
    ),
    on(
        fromStudentsActions.updateSchoolPermissionSuccess,
        fromStudentsActions.updateSchoolPermissionFail,
        (state) => {
            // We just return state as is here,
            // Should we want to update state in any way we can then do it here
            return state;
        }
    ),
    on(fromStudentsActions.setStudentName, (state, { payload }) => {
        const entities = new Map<string, Student>(state.entities.entries());
        entities.forEach((entity) => {
            if (payload.id === entity.userGUID) {
                const originalStudent = state.entities.get(payload.id);
                const student = <Student>{
                    ...originalStudent,
                    firstName: payload.givenName,
                    lastName: payload.surName,
                };
                entities.set(payload.id, student);
            }
        });
        return {
            ...state,
            entities,
        };
    })
);
export function reducer(state: StudentsListState | undefined, action: Action) {
    return studentsListReducer(state, action);
}

export const getStudentsEntities = (state: StudentsListState) => state.entities;
export const getStudentMetrics = (state: StudentsListState) =>
    state.studentMetrics;
export const getStudentsLoading = (state: StudentsListState) => state.loading;
export const getStudentsLoaded = (state: StudentsListState) => state.loaded;
export const getStudentsRawResponse = (state: StudentsListState) =>
    state.rawResponse;
export const getStudentsPaginationParams = (state: StudentsListState) =>
    state.paginationParams;
export const getStudentFilterBoxOpened = (state: StudentsListState) =>
    state.filterBoxOpened;
export const getFidoStudentsData = (state: StudentsListState) =>
    state.fidoStudentsData;
